import { ref, shallowRef, Ref } from 'vue';
import { Moment } from 'moment';
import Client from '@/models/Client';
import { fetchDeliveryOrders } from '@/modules/Logistics/helpers/DeliveryOrderHelper';
import {
    buildDeliveryClientRegionFilter,
    buildDeliveryRegionFilter,
} from '@/modules/Logistics/helpers/DeliveryFiltersHelper';
import type { DeliveryClientOption } from '@/modules/Logistics/interfaces/DeliveryOrderInterfaces';

interface UseDeliveryFiltersOptions {
    selectedOrderIds: Ref<string[]>;
    selectedInternalOrderIds: Ref<number[]>;
    ordersLoading: Ref<boolean>;
    notifyError: (description: string, error: unknown) => void;
}

export function useDeliveryFilters(options: UseDeliveryFiltersOptions) {
    const { selectedOrderIds, selectedInternalOrderIds, ordersLoading, notifyError } = options;

    const selectedDeliveryDates = shallowRef<Moment[]>([]);
    const selectedDeliveryClientId = ref<string | null>(null);
    const selectedOrderDeliveryRegionId = ref<string | null>(null);
    const deliveryClients = ref<DeliveryClientOption[]>([]);
    const deliveryClientSearchLoading = ref(false);

    async function handleDeliveryDatesChange(dates: Moment[]) {
        selectedDeliveryDates.value = dates;

        await refreshDeliveryOrders();
    }

    async function handleDeliveryClientSearch(searchQuery: string) {
        const trimmedSearchQuery = searchQuery.trim();

        if (trimmedSearchQuery.length < 3) {
            deliveryClients.value = [];
            return;
        }

        try {
            deliveryClientSearchLoading.value = true;

            const clients = (await Client.searchAndFilter(
                buildDeliveryClientRegionFilter(selectedOrderDeliveryRegionId.value),
                trimmedSearchQuery,
            )) as Client[];
            deliveryClients.value = createDeliveryClientOptions(clients);
        } catch (error) {
            deliveryClients.value = [];
            notifyError('Failed to fetch clients.', error);
        } finally {
            deliveryClientSearchLoading.value = false;
        }
    }

    async function handleDeliveryClientChange(clientId: string | null) {
        selectedDeliveryClientId.value = clientId;
        await refreshDeliveryOrders();
    }

    async function handleOrderDeliveryRegionChange(deliveryRegionId: string | null) {
        selectedOrderDeliveryRegionId.value = deliveryRegionId;
        await refreshDeliveryOrders();
    }

    async function refreshDeliveryOrders() {
        selectedOrderIds.value = [];
        selectedInternalOrderIds.value = [];

        if (selectedDeliveryDates.value.length === 0) {
            return;
        }

        try {
            ordersLoading.value = true;
            await fetchDeliveryOrders({
                deliveryDates: selectedDeliveryDates.value,
                clientId: selectedDeliveryClientId.value,
                deliveryRegionId: selectedOrderDeliveryRegionId.value,
            });
        } catch (error) {
            notifyError('Failed to fetch orders for the selected date.', error);
        } finally {
            ordersLoading.value = false;
        }
    }

    function resetDeliveryFilters() {
        selectedDeliveryDates.value = [];
        selectedDeliveryClientId.value = null;
        selectedOrderDeliveryRegionId.value = null;
        deliveryClients.value = [];
    }

    function createDeliveryClientOptions(clients: Client[]): DeliveryClientOption[] {
        return clients.map((client) => ({
            id: String(client.id),
            name: client.name || '',
        }));
    }

    return {
        selectedDeliveryDates,
        selectedDeliveryClientId,
        selectedOrderDeliveryRegionId,
        deliveryClients,
        deliveryClientSearchLoading,
        handleDeliveryDatesChange,
        handleDeliveryClientSearch,
        handleDeliveryClientChange,
        handleOrderDeliveryRegionChange,
        resetDeliveryFilters,
    };
}
